import { useCallback, useEffect, useState } from 'react';
import { type To, useNavigate } from 'react-router-dom';

import { QueryClient } from '@tanstack/react-query';

import { clearCache } from 'api/cache';
import { ROUTES } from 'routes/routes';

import { isSessionExpired } from './isSessionExpired';

export function usePrevious<T>(value: T): T | undefined {
    const [prev, setPrev] = useState<T | undefined>(undefined);

    useEffect(() => {
        setPrev(value);
    }, [value]);

    return prev;
}

export function useHandleExpiredSession(queryClient?: QueryClient) {
    const navigate = useNavigate();
    return useCallback(
        error => {
            if (isSessionExpired(error)) {
                clearCache(queryClient);
                navigate({ pathname: ROUTES.LOGIN, search: '?session_expired' });
            }
        },
        [queryClient, navigate]
    );
}

export function usePersistentState<T = any>(
    key: string,
    initialValue: T,
    isSession = true
): [T, React.Dispatch<React.SetStateAction<T>>] {
    const storage = isSession ? sessionStorage : localStorage;
    const [state, setState] = useState<T>(() => {
        try {
            const storageValue = storage.getItem(key);
            if (typeof storageValue !== 'string') {
                storage.setItem(key, JSON.stringify(initialValue));
                return initialValue;
            }
            return JSON.parse(storageValue || 'null');
        } catch {
            return initialValue;
        }
    });

    useEffect(() => {
        try {
            const serializedState = JSON.stringify(state);
            storage.setItem(key, serializedState);
        } catch {
            // ignored
        }
    });

    return [state, setState];
}

export const SKIP_PROMPT_STATE_KEY = '__skipDirtyPrompt';

export function useNavigateAfterSubmit() {
    const navigate = useNavigate();

    return useCallback(
        (to: To, state?: any) => {
            const completeState = { ...state, [SKIP_PROMPT_STATE_KEY]: true };
            navigate(to, { state: completeState });
        },
        [navigate]
    );
}
